import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Modal, Button, ListGroup, Spinner } from 'react-bootstrap';
import { getSecureFileUrl } from '../utils/fileUtils';
import { useAuth } from '../contexts/AuthContext';
import {
    getPlaylistPreferences,
    upsertPlaylistPreference,
    deletePlaylistPreference,
} from '../api/playlistPreferences';

const PlaylistPlayerModal = ({ show, onHide, playlist, eventName }) => {
    const { organization } = useAuth();
    const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentAudioSrc, setCurrentAudioSrc] = useState('');
    const [isLoadingTrack, setIsLoadingTrack] = useState(false);

    // Preferences: map song_file_id -> skip (boolean)
    const [skipPreferences, setSkipPreferences] = useState({});
    const [preferencesLoaded, setPreferencesLoaded] = useState(false);

    const audioRef = useRef(null);
    const audioContextRef = useRef(null);
    const mediaElementSourceRef = useRef(null);

    // Load preferences when modal opens
    useEffect(() => {
        if (show && !preferencesLoaded) {
            getPlaylistPreferences()
                .then((prefs) => {
                    const skipMap = {};
                    prefs.forEach((p) => {
                        skipMap[p.song_file_id] = p.skip;
                    });
                    setSkipPreferences(skipMap);
                    setPreferencesLoaded(true);
                })
                .catch((err) => {
                    console.error('Error loading playlist preferences:', err);
                    setPreferencesLoaded(true);
                });
        }
        if (!show) {
            setPreferencesLoaded(false);
            setSkipPreferences({});
        }
    }, [show, preferencesLoaded]);

    const flatPlaylist = useMemo(() =>
        playlist.flatMap(song =>
            song.files.map(file => ({
                ...file,
                songTitle: song.songTitle,
                organizationName: song.organization?.name,
                skip: skipPreferences[file.id] || false,
            }))
        ),
    [playlist, skipPreferences]);

    const initAudioContext = useCallback(() => {
        if (!audioContextRef.current && audioRef.current) {
            try {
                const context = new (window.AudioContext || window.webkitAudioContext)();
                const source = context.createMediaElementSource(audioRef.current);
                source.connect(context.destination);
                audioContextRef.current = context;
                mediaElementSourceRef.current = source;
            } catch (e) {
                console.error("Failed to initialize AudioContext:", e);
            }
        }
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume();
        }
    }, []);

    const playNext = useCallback(() => {
        if (flatPlaylist.length === 0) return;

        setCurrentTrackIndex(prevIndex => {
            let nextIndex = (prevIndex + 1) % flatPlaylist.length;
            // Skip tracks marked as skip, but avoid infinite loop
            let attempts = 0;
            while (flatPlaylist[nextIndex]?.skip && attempts < flatPlaylist.length) {
                nextIndex = (nextIndex + 1) % flatPlaylist.length;
                attempts++;
            }
            if (attempts >= flatPlaylist.length) {
                // All tracks are skipped — stay at current position
                return prevIndex;
            }
            return nextIndex;
        });
    }, [flatPlaylist]);

    const playPrev = useCallback(() => {
        if (flatPlaylist.length === 0) return;

        setCurrentTrackIndex(prevIndex => {
            let prev = (prevIndex - 1 + flatPlaylist.length) % flatPlaylist.length;
            let attempts = 0;
            while (flatPlaylist[prev]?.skip && attempts < flatPlaylist.length) {
                prev = (prev - 1 + flatPlaylist.length) % flatPlaylist.length;
                attempts++;
            }
            if (attempts >= flatPlaylist.length) {
                return prevIndex;
            }
            return prev;
        });
    }, [flatPlaylist]);

    const togglePlayPause = useCallback(() => {
        initAudioContext();
        if (currentAudioSrc || isLoadingTrack) {
            setIsPlaying(prevIsPlaying => !prevIsPlaying);
        }
    }, [currentAudioSrc, isLoadingTrack, initAudioContext]);

    // Effect to load track data
    useEffect(() => {
        let isMounted = true;
        let objectUrl = null;

        const loadTrack = async () => {
            if (!show || flatPlaylist.length === 0) {
                setCurrentAudioSrc('');
                return;
            }

            setIsLoadingTrack(true);

            const track = flatPlaylist[currentTrackIndex];
            if (!track) {
                setIsLoadingTrack(false);
                return;
            }

            try {
                const secureUrl = await getSecureFileUrl(track.filePath);
                objectUrl = secureUrl;

                if (isMounted) {
                    setCurrentAudioSrc(secureUrl);
                }
            } catch (error) {
                console.error("Error loading track:", error);
                if (isMounted) {
                    setCurrentAudioSrc('');
                }
            } finally {
                if (isMounted) {
                    setIsLoadingTrack(false);
                }
            }
        };

        loadTrack();

        return () => {
            isMounted = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [currentTrackIndex, show, flatPlaylist]);

    // Effect to control play/pause
    useEffect(() => {
        if (audioRef.current) {
            if (isPlaying && currentAudioSrc && !isLoadingTrack) {
                audioRef.current.play().catch(e => {
                    console.error("Audio play failed:", e);
                    setIsPlaying(false);
                });
            } else {
                audioRef.current.pause();
            }
        }
    }, [isPlaying, currentAudioSrc, isLoadingTrack]);

    // Autoplay when modal opens
    useEffect(() => {
        if (show && flatPlaylist.length > 0 && preferencesLoaded) {
            // Find first non-skipped track
            const firstPlayableIndex = flatPlaylist.findIndex(t => !t.skip);
            if (firstPlayableIndex >= 0) {
                setCurrentTrackIndex(firstPlayableIndex);
                setIsPlaying(true);
            } else {
                // Все треки пропущены
                setIsPlaying(false);
            }
        } else if (!show) {
            setIsPlaying(false);
        }
    }, [show, flatPlaylist.length, preferencesLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

    // Media Session Action Handlers
    useEffect(() => {
        if (!show || !('mediaSession' in navigator)) return;

        navigator.mediaSession.setActionHandler('play', togglePlayPause);
        navigator.mediaSession.setActionHandler('pause', togglePlayPause);
        navigator.mediaSession.setActionHandler('previoustrack', playPrev);
        navigator.mediaSession.setActionHandler('nexttrack', playNext);

        return () => {
            if ('mediaSession' in navigator) {
                navigator.mediaSession.setActionHandler('play', null);
                navigator.mediaSession.setActionHandler('pause', null);
                navigator.mediaSession.setActionHandler('previoustrack', null);
                navigator.mediaSession.setActionHandler('nexttrack', null);
                navigator.mediaSession.metadata = null;
            }
        };
    }, [show, togglePlayPause, playPrev, playNext]);

    const handlePlay = () => {
        initAudioContext();
        if ('mediaSession' in navigator) {
            const track = flatPlaylist[currentTrackIndex];
            if (!track) return;

            navigator.mediaSession.metadata = new MediaMetadata({
                title: track.songTitle,
                artist: track.organizationName || (organization ? organization.name : 'EventPlan'),
                album: eventName,
                artwork: [
                    { src: '/event_plan_logo.png', type: 'image/png' }
                ]
            });
        }
    };

    const handleEnded = () => {
        playNext();
    };

    const playTrack = (index) => {
        initAudioContext();
        if (index !== currentTrackIndex) {
            setCurrentTrackIndex(index);
            setIsPlaying(true);
        }
    };

    // Toggle skip preference for a track
    const handleToggleSkip = async (track) => {
        const newSkipValue = !track.skip;
        const isCurrentTrack = track.id === flatPlaylist[currentTrackIndex]?.id;

        // Optimistic UI update
        setSkipPreferences(prev => ({
            ...prev,
            [track.id]: newSkipValue,
        }));

        // If we just marked the currently playing track as skipped, move to next playable track
        if (isCurrentTrack && newSkipValue) {
            setIsPlaying(false);
            let nextIndex = (currentTrackIndex + 1) % flatPlaylist.length;
            let attempts = 0;
            while (flatPlaylist[nextIndex]?.skip && attempts < flatPlaylist.length) {
                nextIndex = (nextIndex + 1) % flatPlaylist.length;
                attempts++;
            }
            if (attempts < flatPlaylist.length) {
                setCurrentTrackIndex(nextIndex);
                setIsPlaying(true);
            }
        }

        try {
            if (newSkipValue) {
                await upsertPlaylistPreference(track.id, true);
            } else {
                await deletePlaylistPreference(track.id);
            }
        } catch (err) {
            // Revert on error
            setSkipPreferences(prev => ({
                ...prev,
                [track.id]: track.skip,
            }));
            console.error('Error updating playlist preference:', err);
        }
    };

    const currentTrack = flatPlaylist[currentTrackIndex];
    const allSkipped = flatPlaylist.length > 0 && flatPlaylist.every(t => t.skip);

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Плейлист: {eventName}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {flatPlaylist.length > 0 ? (
                    <>
                        <div className="text-center mb-3">
                            <h5>{currentTrack?.songTitle}</h5>
                            <p className="text-muted">{currentTrack?.original_filename}</p>
                            {allSkipped ? (
                                <div className="alert alert-warning mb-3">
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                    Все треки пропущены. Измените настройки, чтобы воспроизвести музыку.
                                </div>
                            ) : isLoadingTrack ? (
                                <div className="d-flex justify-content-center align-items-center" style={{ height: '54px' }}>
                                    <Spinner animation="border" />
                                    <span className="ms-2">Загрузка трека...</span>
                                </div>
                            ) : (
                                <audio
                                    ref={audioRef}
                                    src={currentAudioSrc}
                                    onEnded={handleEnded}
                                    onPlay={handlePlay}
                                    controls
                                    crossOrigin="anonymous" // Required for MediaElementSource
                                    autoPlay={isPlaying}
                                    style={{ width: '100%' }}
                                />
                            )}
                        </div>
                        <div className="d-flex justify-content-center align-items-center mb-3">
                            <Button variant="outline-secondary" onClick={playPrev} disabled={isLoadingTrack || allSkipped}><i className="fas fa-step-backward"></i></Button>
                            <Button variant="primary" onClick={togglePlayPause} className="mx-3" disabled={isLoadingTrack || allSkipped}>
                                {isPlaying ? <i className="fas fa-pause"></i> : <i className="fas fa-play"></i>}
                            </Button>
                            <Button variant="outline-secondary" onClick={playNext} disabled={isLoadingTrack || allSkipped}><i className="fas fa-step-forward"></i></Button>
                        </div>
                        <ListGroup style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            {playlist.map((song, songIndex) => (
                                <React.Fragment key={song.songTitle + songIndex}>
                                    <ListGroup.Item variant="dark" className="mt-2">{song.songTitle}</ListGroup.Item>
                                    {song.files.map(file => {
                                        const trackGlobalIndex = flatPlaylist.findIndex(t => t.id === file.id);
                                        const isSkipped = flatPlaylist[trackGlobalIndex]?.skip;
                                        return (
                                            <ListGroup.Item
                                                key={file.id}
                                                action
                                                active={trackGlobalIndex === currentTrackIndex}
                                                onClick={() => playTrack(trackGlobalIndex)}
                                                disabled={isLoadingTrack}
                                                className="d-flex justify-content-between align-items-center"
                                                style={isSkipped ? { opacity: 0.5, backgroundColor: '#f8f9fa' } : {}}
                                            >
                                                <span className="flex-grow-1">
                                                    {isSkipped && <i className="fas fa-volume-mute text-muted me-2"></i>}
                                                    {file.original_filename || 'Трек'}
                                                </span>
                                                <span className="ms-2">
                                                    <Button
                                                        variant={isSkipped ? 'outline-danger' : 'outline-secondary'}
                                                        size="sm"
                                                        onClick={(e) => { e.stopPropagation(); handleToggleSkip(flatPlaylist[trackGlobalIndex]); }}
                                                        title={isSkipped ? 'Воспроизводить этот трек' : 'Пропускать этот трек'}
                                                        className="btn-sm px-2 py-0"
                                                        style={{ lineHeight: '1.5', fontSize: '0.8rem' }}
                                                    >
                                                        {isSkipped ? (
                                                            <i className="fas fa-volume-mute"></i>
                                                        ) : (
                                                            <i className="fas fa-volume-up"></i>
                                                        )}
                                                    </Button>
                                                </span>
                                                {trackGlobalIndex === currentTrackIndex && isLoadingTrack && <Spinner animation="border" size="sm" className="ms-2"/>}
                                            </ListGroup.Item>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </ListGroup>
                    </>
                ) : (
                    <p>В этом событии нет песен с MP3 файлами.</p>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default PlaylistPlayerModal;
